import { useEffect } from "react";

import { ServiceError, useRemoteConfigContext } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import { ServiceErrorCode } from "../types";
import useAccount from "./useAccount";
import { useProject } from "./useProject";
import { useTracker } from "./useTracker";

export const useProjectPageTracking = (isInviteAcceptancePending = false) => {
  const tracker = useTracker();
  const { projectId } = useParams();
  const { data: accountData } = useAccount();
  const { data: projectData, error: projectError } = useProject(isInviteAcceptancePending);
  const { initialLoadFinished, isLoading } = useRemoteConfigContext();

  useEffect(() => {
    if (!initialLoadFinished || isLoading) {
      return;
    }

    if (projectData) {
      tracker.trackProjectPageLoaded({
        accessStatus: "success",
        projectId: projectData.project.projectId,
        projectTeamId: projectData.teamId,
        hasReviewLink: Boolean(projectData.projectViewLinks?.length),
      });
      return;
    }

    if (!(projectError instanceof ServiceError)) {
      return;
    }

    let accessStatus: "not_found" | "pending_access" | "unverified_email" | "forbidden" | undefined;

    if (projectError.hasCode(ServiceErrorCode.ProjectNotFound)) {
      accessStatus = "not_found";
    } else if (projectError.hasCode(ServiceErrorCode.ProjectPendingAccess)) {
      accessStatus = "pending_access";
    } else if (projectError.hasCode(ServiceErrorCode.ProjectForbidden) && accountData) {
      accessStatus = !accountData.emailVerified ? "unverified_email" : "forbidden";
    }

    if (accessStatus) {
      tracker.trackProjectPageLoaded({
        accessStatus,
        projectId: projectId ?? "",
      });
    }
  }, [accountData, projectData, projectError, projectId, tracker, initialLoadFinished, isLoading]);
};
